import { Base } from '@studiometa/js-toolkit';
import {
  addStyle,
  removeStyle,
  addClass,
  removeClass,
  scrollTo,
  historyPush,
  nextTick,
} from '@studiometa/js-toolkit/utils';
import { Transition } from '@studiometa/ui';
import PageTransitionLink from './PageTransitionLink.js';

/**
 * Parge an HTML string into a DOM object.
 * @param {string} [string=''] Raw HTML
 * @returns {Document}
 */
function parseHTML(string = '') {
  return new DOMParser().parseFromString(string, 'text/html');
}

/**
 * PageTransition
 */
export default class PageTransition extends Base {
  /**
   * Config.
   * @type {import('@studiometa/js-toolkit').BaseConfig}
   */
  static config = {
    name: 'PageTransition',
    options: {
      priority: {
        type: Number,
        default: 0,
      },
    },
    components: {
      // @ts-ignore
      PageTransitionAnimation: Transition,
      PageTransitionLink,
    },
  };

  lastUrl = null;

  /**
   * The fetch cache.
   * @type {Map<string,{promise: Promise<string>; status: 'pending' | 'resolved' | 'error'; content: string}>}
   */
  cache = new Map();

  /**
   * Fetch
   * @param {string} target
   * @returns {Promise<string>}
   */
  async fetch(target) {
    const cached = this.cache.get(target);

    if (cached) {
      if (cached.status === 'pending') {
        return cached.promise;
      }

      return cached.content;
    }

    const promise = fetch(target).then((response) => response.text());

    try {
      this.cache.set(target, {
        promise,
        status: 'pending',
        content: undefined,
      });

      const content = await promise;

      this.cache.set(target, {
        promise,
        status: 'resolved',
        content,
      });

      return content;
    } catch (err) {
      this.cache.delete(target);
      throw err;
    }
  }

  /**
   * getNextDocFrom target url
   * @param {string} target
   * @returns {Promise<Document>}
   */
  async getNextDocFrom(target) {
    // Get next page
    const response = await this.fetch(target);

    // Get next doc
    return parseHTML(response);
  }

  /**
   * updateDocument
   * @param {Document} nextDoc
   * @param {string} target
   */
  async updateDocument(nextDoc, target) {
    // Update <html> and <body> classes
    document.documentElement.className = nextDoc.documentElement.className;
    document.body.className = nextDoc.body.className;

    // Update Document title
    document.title = nextDoc.title;

    // Push history
    this.lastUrl = window.location.href;
    const parsedUrl = new URL(target);
    historyPush({ path: parsedUrl.pathname, search: parsedUrl.searchParams });

    // replace secondary Action
    const currentSecondaryAction = document.querySelector('[data-secondary-action]');
    const nextSecondaryAction = nextDoc.querySelector('[data-secondary-action]');
    currentSecondaryAction.replaceWith(nextSecondaryAction);

    // Update Header component
    const currentHeader = document.querySelector('header');
    const nextHeader = nextDoc.querySelector('header');
    currentHeader.dataset.component = nextHeader.dataset.component;

    // Reload App
    await this.$root.$update();
  }

  /**
   * Cleanup
   * @param {Document} nextDoc
   */
  cleanup(nextDoc) {
    // Update priority
    this.$options.priority = Number.parseInt(nextDoc.body.dataset.optionPriority ?? '0', 10);

    // Remove preload class
    nextTick(() => {
      setTimeout(() => {
        document.documentElement.classList.remove('preload');
      }, 1000);
    });
  }

  /**
   * GoTo
   * @param {string} target
   * @param {boolean} force
   */
  async goTo(target, force) {
    try {
      // ScrollTo if it is the same page
      if (!force && target === window.location.href) {
        this.$root.$Header?.close();
        await scrollTo(document.body);
        return;
      }

      let nextDoc = await this.getNextDocFrom(target);

      // Get and test the next transition priority
      const nextPriority = Number.parseInt(nextDoc.body.dataset.optionPriority ?? '0', 10);
      const nextPriorityIsHigher = this.$options.priority < nextPriority;

      // Get current main and footer
      const currentMain = document.querySelector('main');
      const currentFooter = document.querySelector('footer');

      // Get next main and footer
      const nextMain = nextDoc.querySelector('main');
      const nextFooter = nextDoc.querySelector('footer');

      if (this.$root.$children.RoomTypeItem && this.$root.$children.RoomTypeItem.length > 0) {
        this.$root.$children.RoomTypeItem.forEach((instance) => instance.$destroy());
      }

      // Simply replace main and close menu if open
      if (this.$root.$Header.$options.open) {
        // Replace main
        currentMain.replaceWith(nextMain);

        // Append footer if needed
        if (!currentFooter && nextFooter) {
          document.body.append(nextFooter);
        }

        // Remove footer if needed
        if (!nextFooter && currentFooter) {
          currentFooter.remove();
        }

        window.scrollTo(0, 0);

        // Close menu
        await this.$root.$Header.close();

        // Update document
        await this.updateDocument(nextDoc, target);

        // Clean up
        this.cleanup(nextDoc);

        // Remove the nextDoc
        nextDoc = null;
        return;
      }

      // Redirect if same priority
      if (this.$options.priority === nextPriority) {
        window.location.href = target;
        return;
      }

      // Show the navbar
      await this.$root.$Navbar?.show();

      // Prevent scroll
      addClass(document.documentElement, 'overflow-hidden');

      // Setup the upper main styles
      addStyle(nextPriorityIsHigher ? nextMain : currentMain, {
        position: 'fixed',
        top: '0',
        left: '0',
        right: '0',
        zIndex: '10',
        background: 'transparent',
        pointerEvents: 'none',
      });

      // Append the next main
      currentMain.after(nextMain);

      // Update document
      await this.updateDocument(nextDoc, target);

      // Get Animations
      const animations = this.$children.PageTransitionAnimation.filter((instance) =>
        nextPriorityIsHigher ? nextMain.contains(instance.$el) : !nextMain.contains(instance.$el),
      );

      // Trigger Transition animations
      await Promise.all(
        animations.map((item) => (nextPriorityIsHigher ? item.enter() : item.leave())),
      );

      // Reset pointer events
      removeStyle(nextPriorityIsHigher ? nextMain : currentMain, {
        pointerEvents: 'none',
      });

      // Remove old main
      currentMain.remove();

      // Append footer if needed
      if (!currentFooter && nextFooter) {
        document.body.append(nextFooter);
      }

      // Remove footer if needed
      if (!nextFooter && currentFooter) {
        currentFooter.remove();
      }

      // Reset main styles
      if (nextPriorityIsHigher) {
        removeStyle(nextMain, {
          position: 'fixed',
          top: '0',
          left: '0',
          right: '0',
          zIndex: '10',
          background: 'transparent',
        });
        window.scrollTo(0, 0);
      }

      removeClass(document.documentElement, 'overflow-hidden');

      // Clean up
      this.cleanup(nextDoc);

      // Remove the nextDoc
      nextDoc = null;
    } catch (err) {
      // Redirect if transition failed
      console.error(err);
      window.location.href = target;
    }
  }

  /**
   * Mounted
   */
  mounted() {
    this.popstateHandler = () => {
      this.goTo(window.location.href, true);
    };

    window.addEventListener('popstate', this.popstateHandler);
  }

  /**
   * Destroyed
   */
  destroyed() {
    window.removeEventListener('popstate', this.popstateHandler);
  }
}
